import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SegmentedControl, Select, Checkbox, Button, TextInput } from '@mantine/core';
import { useServiceOrders, useServices, usePersons, useBusinesses, useInventoryItems } from '@/hooks/api-hooks';
import { getUrlParam, setWebTitle, updateUrlParams } from '@/lib/format-utils';
import { ServiceOrder, Service, Person, Business, InventoryItem } from '@turizi/shared';
import DatePickerCustom from '@/components/custom-date-picker';
import dayjs from 'dayjs';
import SearchInput from '@/components/search-input';
import { SkeletonList, ShowPaxGroup } from '@/components/snippets';
import { EDIT_SERVICE_ORDER_PATH } from '@/lib/navigation';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { IconAdjustments, IconSearch, IconX, IconList } from '@tabler/icons-react';
import { TagIcon } from '@/lib/tag-utils';
import CardModal from '@/components/card-modal';

interface MatrixColumn {
    id: string | number;
    title: string;
    subtitle?: string;
    orders: ServiceOrder[];
}

export default function ServiceOrdersMatrixPage() {
    const { t } = useTranslation();

    setWebTitle(t('service_orders_matrix'));

    // 1. Initial State from URL
    const initialStart = getUrlParam('start_date') || dayjs().format('YYYY-MM-DD');
    const initialEnd = getUrlParam('end_date') || dayjs().add(3, 'day').format('YYYY-MM-DD');
    const initialView = (getUrlParam('view') as 'daily' | 'hourly') || 'daily';
    const urlItemType = getUrlParam('item_type');
    const initialItemType: 'service' | 'pax' | 'provider' | 'inventory' =
        urlItemType === 'service' || urlItemType === 'pax' || urlItemType === 'provider' || urlItemType === 'inventory'
            ? urlItemType
            : 'inventory';
    const initialSelectedIds = getUrlParam('selected_ids')?.split(',').filter(Boolean) || [];

    const [dateRange, setDateRange] = useState<[string | null, string | null]>([initialStart, initialEnd]);
    const [viewMode, setViewMode] = useState<'daily' | 'hourly'>(initialView);
    const [itemType, setItemType] = useState<'service' | 'pax' | 'provider' | 'inventory'>(initialItemType);
    const [selectedItemIds, setSelectedItemIds] = useState<string[]>(initialSelectedIds);
    const [searchText, setSearchText] = useState('');
    const [modalSearch, setModalSearch] = useState('');
    const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
    const [shouldAutoSelect, setShouldAutoSelect] = useState(!getUrlParam('selected_ids'));

    // Clamp range if viewMode is hourly to prevent rendering lag
    useEffect(() => {
        if (viewMode === 'hourly' && dateRange[0] && dateRange[1]) {
            const diff = dayjs(dateRange[1]).diff(dayjs(dateRange[0]), 'day');
            if (diff > 3) {
                setDateRange([dateRange[0], dayjs(dateRange[0]).add(2, 'day').format('YYYY-MM-DD')]);
            }
        }
    }, [viewMode]);

    useEffect(() => {
        if (dateRange[0] && dateRange[1]) {
            updateUrlParams({
                start_date: dateRange[0],
                end_date: dateRange[1],
                view: viewMode,
                item_type: itemType,
                selected_ids: selectedItemIds.join(',') || undefined,
            });
        }
    }, [dateRange, viewMode, itemType, selectedItemIds]);

    // 2. API Queries
    const { data: servicesRes } = useServices(undefined, { enabled: itemType === 'service' });
    const { data: personsRes } = usePersons(undefined, { enabled: itemType === 'pax' });
    const { data: businessesRes } = useBusinesses(undefined, { enabled: itemType === 'provider' });
    const { data: inventoryRes } = useInventoryItems(undefined, { enabled: itemType === 'inventory' });
    const { data: serviceOrdersRes, isLoading: serviceOrdersLoading } = useServiceOrders({
        start_date: dateRange[0] || undefined,
        end_date: dateRange[1] || undefined,
    });

    const services = servicesRes?.data || [];
    const persons = personsRes?.data || [];
    const businesses = businessesRes?.data || [];
    const inventory = inventoryRes?.data || [];
    const serviceOrders = serviceOrdersRes?.data || [];

    // 3. Time bounds helper for ServiceOrder
    const getOrderTimeBounds = (order: ServiceOrder) => {
        const coreBookings = order.service_order_bookings?.map(sob => sob.service_booking).filter(Boolean) || [];
        if (coreBookings.length === 0) {
            return {
                start_day: order.start_day ? dayjs(order.start_day).format('YYYY-MM-DD') : undefined,
                end_day: order.end_day ? dayjs(order.end_day).format('YYYY-MM-DD') : undefined,
                start_hour: order.start_hour || '00:00:00',
                end_hour: order.end_hour || '23:59:59',
            };
        }

        // Sort core bookings by start_day and start_hour
        const sorted = [...coreBookings].sort((a, b) => {
            const dayA = a.start_day || '';
            const dayB = b.start_day || '';
            if (dayA !== dayB) return dayA.localeCompare(dayB);
            const hrA = a.start_hour || '00:00:00';
            const hrB = b.start_hour || '00:00:00';
            return hrA.localeCompare(hrB);
        });

        const start_day = sorted[0].start_day;
        const start_hour = sorted[0].start_hour || '00:00:00';

        // Sort core bookings by end_day and end_hour
        const sortedEnd = [...coreBookings].sort((a, b) => {
            const dayA = a.end_day || a.start_day || '';
            const dayB = b.end_day || b.start_day || '';
            if (dayA !== dayB) return dayA.localeCompare(dayB);
            const hrA = a.end_hour || '23:59:59';
            const hrB = b.end_hour || '23:59:59';
            return hrA.localeCompare(hrB);
        });

        const end_day = sortedEnd[sortedEnd.length - 1].end_day || sortedEnd[sortedEnd.length - 1].start_day;
        const end_hour = sortedEnd[sortedEnd.length - 1].end_hour || '23:59:59';

        return { start_day, end_day, start_hour, end_hour };
    };

    // 4. Time Slots Generation (Y-Axis)
    const timeSlots = useMemo(() => {
        const slots: Date[] = [];
        if (!dateRange[0] || !dateRange[1]) return slots;

        const effectiveStart = new Date(dateRange[0]);
        const effectiveEnd = new Date(dateRange[1]);

        effectiveStart.setHours(0, 0, 0, 0);
        effectiveEnd.setHours(23, 59, 59, 999);

        let current = new Date(effectiveStart);

        if (viewMode === 'hourly') {
            const endTimestamp = effectiveEnd.getTime();
            while (current.getTime() <= endTimestamp) {
                slots.push(new Date(current));
                current.setHours(current.getHours() + 1);
            }
        } else {
            // Daily
            while (current <= effectiveEnd) {
                slots.push(new Date(current));
                current.setDate(current.getDate() + 1);
            }
        }
        return slots;
    }, [dateRange, viewMode]);

    // 5. Select options based on Columns Type (Y-Axis select data)
    const itemOptions = useMemo(() => {
        if (itemType === 'service') {
            return services.map(s => ({ value: String(s.id), label: ServiceTitleStatic(s), raw: s }));
        }
        if (itemType === 'pax') {
            return persons.map(p => ({ value: String(p.id), label: p.name, raw: p }));
        }
        if (itemType === 'provider') {
            return businesses.map(b => ({ value: String(b.id), label: b.name, raw: b }));
        }
        if (itemType === 'inventory') {
            return inventory.map(i => ({ value: String(i.id), label: `${i.name} - ${i.full_path}`, raw: i }));
        }
        return [];
    }, [itemType, services, persons, businesses, inventory]);

    // Auto-select all items of the current type when they are loaded, if triggered
    useEffect(() => {
        if (shouldAutoSelect && itemOptions.length > 0) {
            setSelectedItemIds(itemOptions.map(opt => opt.value));
            setShouldAutoSelect(false);
        }
    }, [itemOptions, shouldAutoSelect]);

    const renderSelectOption = ({ option }: { option: any }) => {
        const raw = option.raw;
        if (!raw) return option.label;

        if (itemType === 'service') {
            const service = raw as Service;
            return (
                <div className="flex items-center gap-2 w-full py-0.5">
                    <TagIcon name={service.category || 'services'} size="xxs" hideText noBackground />
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-semibold text-slate-800">
                            {ServiceTitleStatic(service)}
                        </span>
                        <span className="text-[10px] text-slate-400">
                            #{service.id} &middot; {t(service.category)}
                        </span>
                    </div>
                </div>
            );
        }

        if (itemType === 'pax') {
            const person = raw as Person;
            return (
                <div className="flex items-center gap-2 w-full py-0.5">
                    <TagIcon name="persons" size="xxs" hideText noBackground />
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-semibold text-slate-800">{person.name}</span>
                        {(person.email || person.phone) && (
                            <span className="text-[10px] text-slate-400">
                                {[person.email, person.phone].filter(Boolean).join(' · ')}
                            </span>
                        )}
                    </div>
                </div>
            );
        }

        if (itemType === 'provider') {
            const business = raw as Business;
            return (
                <div className="flex items-center gap-2 w-full py-0.5">
                    <TagIcon name="businesses" size="xxs" hideText noBackground />
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-semibold text-slate-800">{business.name}</span>
                        <span className="text-[10px] text-slate-400">
                            {[t('provider'), business.city, business.country_code].filter(Boolean).join(' · ')}
                        </span>
                    </div>
                </div>
            );
        }

        if (itemType === 'inventory') {
            const item = raw as InventoryItem;
            return (
                <div className="flex items-center gap-2 w-full py-0.5">
                    <TagIcon name="inventory" size="xxs" hideText noBackground />
                    <div className="flex flex-col flex-1 min-w-0">
                        <span className="text-xs font-semibold text-slate-800">
                            {item.name}
                        </span>
                        <span className="text-[10px] text-slate-400">
                            #{item.id} &middot; {item.full_path}
                        </span>
                    </div>
                </div>
            );
        }

        return option.label;
    };

    const toggleItem = (val: string) => {
        setSelectedItemIds(prev =>
            prev.includes(val) ? prev.filter(id => id !== val) : [...prev, val]
        );
    };

    const filteredModalOptions = useMemo(() => {
        if (!modalSearch.trim()) return itemOptions;
        const q = modalSearch.toLowerCase().trim();
        return itemOptions.filter(opt => opt.label.toLowerCase().includes(q));
    }, [itemOptions, modalSearch]);

    const handleSelectAll = () => {
        if (modalSearch.trim()) {
            const visibleIds = filteredModalOptions.map(opt => opt.value);
            setSelectedItemIds(prev => Array.from(new Set([...prev, ...visibleIds])));
        } else {
            setSelectedItemIds(itemOptions.map(opt => opt.value));
        }
    };

    const handleClearSelection = () => {
        if (modalSearch.trim()) {
            const visibleIds = new Set(filteredModalOptions.map(opt => opt.value));
            setSelectedItemIds(prev => prev.filter(id => !visibleIds.has(id)));
        } else {
            setSelectedItemIds([]);
        }
    };

    // Pre-computed orders with extracted bookings to prevent redundant parsing across loops
    const parsedServiceOrders = useMemo(() => {
        return serviceOrders.map(order => {
            const coreBookings = order.service_order_bookings?.map(sob => sob.service_booking).filter(Boolean) || [];
            const specialBookings = order.service_bookings || [];
            const allBookings = [...coreBookings, ...specialBookings];
            return {
                order,
                coreBookings,
                specialBookings,
                allBookings,
            };
        });
    }, [serviceOrders]);

    // 6. Columns Generation (X-Axis)
    const columns = useMemo(() => {
        const list: MatrixColumn[] = [];
        if (!selectedItemIds.length) return list;

        selectedItemIds.forEach(val => {
            if (itemType === 'service') {
                const service = services.find(s => String(s.id) === val);
                if (service) {
                    const id = service.id;
                    const title = ServiceTitleStatic(service);
                    const subtitle = ' #' + service.id + ' - ' + t(service.category);
                    const colOrders = parsedServiceOrders
                        .filter(({ allBookings }) => allBookings.some(b => b.service_id === id))
                        .map(({ order }) => order);
                    list.push({ id, title, subtitle, orders: colOrders });
                }

            } else if (itemType === 'pax') {
                const person = persons.find(p => String(p.id) === val);
                if (person) {
                    const id = person.id;
                    const title = person.name;
                    const subtitle = person.email || person.phone || undefined;
                    const colOrders = parsedServiceOrders
                        .filter(({ order, allBookings }) => {
                            const isStaff = order.service_order_persons?.some(sop => sop.person_id === id || sop.person?.id === id);
                            const isOrderPax = order.service_order_pax_groups?.some(sog => sog.pax_group?.persons?.some((p: any) => p.id === id));
                            const isBookingPax = allBookings.some(b =>
                                b.pax_groups?.some(pg => pg.pax_group?.persons?.some((p: any) => p.id === id))
                            );
                            return isStaff || isOrderPax || isBookingPax;
                        })
                        .map(({ order }) => order);
                    list.push({ id, title, subtitle, orders: colOrders });
                }
            } else if (itemType === 'provider') {
                const provId = Number(val);
                const business = businesses.find(b => b.id === provId);
                if (business) {
                    const id = provId;
                    const title = business.name;
                    const subtitle = business.city + ', ' + business.country_code;
                    const colOrders = parsedServiceOrders
                        .filter(({ allBookings }) =>
                            allBookings.some(b => b.selected_provider_id === provId)
                        )
                        .map(({ order }) => order);
                    list.push({ id, title, subtitle, orders: colOrders });
                }
            } else if (itemType === 'inventory') {
                const item = inventory.find(i => String(i.id) === val);
                if (item) {
                    const id = item.id;
                    const title = item.name;
                    const subtitle = '#' + item.id + ' - ' + item.full_path;
                    const colOrders = parsedServiceOrders
                        .filter(({ order }) => order.service_order_inventory_items?.some(soii => soii.inventory_item_id === id))
                        .map(({ order }) => order);
                    list.push({ id, title, subtitle, orders: colOrders });
                }
            }
        });

        return list.sort((a, b) => a.title.localeCompare(b.title));
    }, [selectedItemIds, itemType, services, persons, businesses, inventory, parsedServiceOrders, t]);

    // 7. Search Filtering
    const filteredColumns = useMemo(() => {
        if (!searchText.trim()) return columns;
        const query = searchText.toLowerCase().trim();
        return columns.filter(col =>
            col.title.toLowerCase().includes(query) ||
            col.subtitle?.toLowerCase().includes(query)
        );
    }, [columns, searchText]);

    // Helper functions
    const getDayShort = (date: Date) => {
        const dayKeys = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
        return t(`days_of_week_short.${dayKeys[date.getDay()]}`);
    };

    const columnWidth = 260;
    const timeColumnWidth = 120;

    return (
        <>
            <div className='space-y-2'>
                {/* Filters Row */}
                <div className="flex flex-wrap justify-between items-end gap-3">
                    <div className="flex flex-wrap gap-2 items-end">
                        <DatePickerCustom
                            value={dateRange as [string, string]}
                            onChange={(val) => setDateRange(val as [string, string])}
                            clearable={false}
                            className='font-bold'
                        />
                        <Select
                            label={t('horizontal_axis_items')}
                            data={[
                                { value: 'service', label: t('services') },
                                { value: 'pax', label: t('paxs') },
                                { value: 'provider', label: t('selected_provider') },
                                { value: 'inventory', label: t('inventory') }
                            ]}
                            value={itemType}
                            onChange={(val) => {
                                setItemType(val as any);
                                setSelectedItemIds([]);
                                setShouldAutoSelect(true);
                            }}
                            allowDeselect={false}
                        />
                        <div className="flex flex-col gap-1">
                            <span className="text-xs font-semibold text-gray-500">
                                {t('columns')}
                            </span>
                            <Button
                                variant="default"
                                onClick={() => {
                                    setModalSearch('');
                                    setIsSelectionModalOpen(true);
                                }}
                                leftSection={<IconList size={16} />}
                                className="h-[36px] px-3 font-medium text-slate-700 hover:bg-slate-50 border-slate-200"
                            >
                                {selectedItemIds.length > 0
                                    ? `${selectedItemIds.length} ${t('items_selected', 'selected')}`
                                    : t('select_items_placeholder', 'Pick items to show as columns')}
                            </Button>
                        </div>

                        <SegmentedControl
                            value={viewMode}
                            onChange={(v) => setViewMode(v as 'daily' | 'hourly')}
                            data={[
                                { label: t('day'), value: 'daily' },
                                { label: t('hour'), value: 'hourly' },
                            ]}
                        />
                    </div>
                </div>

                {/* Column Search */}
                <SearchInput
                    size="sm"
                    onSearch={(text) => setSearchText(text)}
                />

                {/* Visual Matrix Container */}
                {serviceOrdersLoading ? (
                    <SkeletonList />
                ) : !selectedItemIds.length ? (
                    <div className="flex flex-col items-center justify-center h-[calc(100vh-250px)] border border-dashed border-slate-200 rounded-xl bg-white p-8 text-center shadow-sm">
                        <div className="p-4 bg-slate-50 rounded-full text-slate-400 mb-3">
                            <IconAdjustments size={32} stroke={1.5} />
                        </div>
                        <h3 className="text-sm font-semibold text-slate-800 mb-1">
                            {t('no_columns_selected')}
                        </h3>
                        <p className="text-xs text-slate-500 max-w-sm">
                            {t('select_columns_instruction')}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col h-[calc(100vh-250px)] border border-slate-200/50 rounded-xl bg-white overflow-hidden shadow-sm">
                        <div className="flex-1 overflow-auto relative [scrollbar-width:thin]">
                            <div style={{ minWidth: '100%', width: `max(100%, ${filteredColumns.length * columnWidth + timeColumnWidth}px)` }}>

                                {/* Header (Sticky) */}
                                <div className="sticky top-0 z-30 flex bg-slate-50 border-b border-slate-200/60 h-[100px] shadow-sm">
                                    <div className="sticky left-0 z-40 w-[120px] flex-shrink-0 bg-slate-50 border-r border-slate-200/50 flex items-center justify-center font-semibold text-[10px] text-slate-400 uppercase tracking-wider shadow-[4px_0_8px_-4px_rgba(0,0,0,0.05)]">
                                        {t('time')}
                                    </div>
                                    <div className="flex">
                                        {filteredColumns.map(col => (
                                            <div key={col.id} style={{ width: columnWidth }} className="flex-shrink-0 border-r border-slate-200/40 flex flex-col justify-center px-2 bg-slate-50">
                                                <span className="font-medium text-slate-700 text-xs" title={col.title}>
                                                    {col.title}
                                                </span>
                                                {col.subtitle && (
                                                    <span className="text-[9px] text-slate-400 font-normal uppercase tracking-wider mt-0.5">
                                                        {col.subtitle}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Body Rows */}
                                <div>
                                    {timeSlots.map((slot, rowIndex) => (
                                        <div key={rowIndex} className="flex border-b border-slate-100/50 hover:bg-slate-50/20">

                                            {/* Time Column (Sticky) */}
                                            <div className="sticky left-0 z-20 w-[120px] flex-shrink-0 bg-white border-r border-slate-200/50 flex flex-col justify-center items-center px-2 py-3 shadow-[4px_0_8px_-4px_rgba(0,0,0,0.04)]">
                                                <span className="font-semibold text-slate-700 text-sm">
                                                    {viewMode === 'daily' ? dayjs(slot).format('DD MMM') : dayjs(slot).format('HH:00')}
                                                </span>
                                                <span className="text-[9px] text-slate-400 font-normal uppercase tracking-wider mt-0.5">
                                                    {viewMode === 'daily' ? getDayShort(slot) : dayjs(slot).format('DD MMM')}
                                                </span>
                                            </div>

                                            {/* Cells */}
                                            <div className="flex">
                                                {filteredColumns.map(col => {
                                                    const cellOrders = col.orders.filter(order => {
                                                        const { start_day, end_day, start_hour, end_hour } = getOrderTimeBounds(order);
                                                        if (!start_day) return false;

                                                        if (viewMode === 'daily') {
                                                            const orderStart = dayjs(start_day);
                                                            const orderEnd = dayjs(end_day || start_day);
                                                            const slotDate = dayjs(slot);
                                                            return !slotDate.isBefore(orderStart, 'day') && !slotDate.isAfter(orderEnd, 'day');
                                                        } else {
                                                            const slotStart = dayjs(slot);
                                                            const slotEnd = slotStart.add(1, 'hour');
                                                            const orderStart = dayjs(`${start_day}T${start_hour || '00:00:00'}`);
                                                            const orderEnd = dayjs(`${end_day || start_day}T${end_hour || '23:59:59'}`);
                                                            return orderStart.isBefore(slotEnd) && orderEnd.isAfter(slotStart);
                                                        }
                                                    });

                                                    return (
                                                        <div key={col.id} style={{ width: columnWidth }} className="flex-shrink-0 border-r border-slate-100/50 p-1 flex flex-col gap-1.5 justify-start">
                                                            {cellOrders.map(order => {
                                                                const coreBookings = order.service_order_bookings?.map(sob => sob.service_booking).filter(Boolean) || [];
                                                                const specialBookings = order.service_bookings || [];
                                                                const inventoryItems = order.service_order_inventory_items?.map(soii => soii.inventory_item).filter(Boolean) || [];
                                                                const bookingsNames = [...new Set(coreBookings.map(b => ServiceTitleStatic(b.service_data)))];

                                                                return (
                                                                    <div
                                                                        key={order.id}
                                                                        className="p-1 bg-gray-50 hover:bg-gray-200 rounded-md cursor-pointer flex flex-col gap-0.5 text-[10px]"
                                                                        onClick={() => {
                                                                            window.open(EDIT_SERVICE_ORDER_PATH.replace(':id', order.id.toString()), '_blank');
                                                                        }}
                                                                    >
                                                                        <div className="flex justify-between items-start gap-1">
                                                                            <span className="text-[10px] font-bold text-slate-800 animate-none">
                                                                                #{order.id}
                                                                            </span>
                                                                            <ShowPaxGroup
                                                                                adults={order.service_order_bookings?.reduce((acc, sob) => acc + (sob.adults || 0), 0) || 0}
                                                                                guests={order.service_order_bookings?.reduce((acc, sob) => acc + (sob.guests || 0), 0) || 0}
                                                                                seniors={order.service_order_bookings?.reduce((acc, sob) => acc + (sob.seniors || 0), 0) || 0}
                                                                                children={order.service_order_bookings?.reduce((acc, sob) => acc + (sob.children || 0), 0) || 0}
                                                                                infants={order.service_order_bookings?.reduce((acc, sob) => acc + (sob.infants || 0), 0) || 0}
                                                                                className="!p-0 !bg-transparent"
                                                                            />
                                                                        </div>

                                                                        {/* Bookings Summary */}
                                                                        {coreBookings.length > 0 && (
                                                                            <div className="flex flex-col gap-0.5 text-[9px] font-medium">
                                                                                <ul className="leading-tight list-disc list-inside">
                                                                                    {bookingsNames.map((n, i) => (
                                                                                        <li key={i}>
                                                                                            {n}
                                                                                        </li>
                                                                                    ))}
                                                                                </ul>
                                                                            </div>
                                                                        )}

                                                                        {/* Inventory Items */}
                                                                        {inventoryItems.length > 0 && (
                                                                            <div className="text-[9px] text-gray-400 font-bold">
                                                                                {inventoryItems.map(item => item.name).join(', ')}
                                                                            </div>
                                                                        )}

                                                                        {/* Special Bookings */}
                                                                        {specialBookings.length > 0 && (
                                                                            <div className="flex flex-col gap-0.5 text-[9px]">
                                                                                {specialBookings.map(b => (
                                                                                    <div key={b.id} className="text-green-700 flex flex-col gap-0.5">
                                                                                        <div className="font-normal">
                                                                                            {ServiceTitleStatic(b.service_data)}: <span className='font-bold'>{b.selected_provider?.name || '-'}</span>
                                                                                        </div>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                        </div>
                                    ))}
                                </div>

                            </div>
                        </div>
                    </div>
                )}
            </div>

            <CardModal
                opened={isSelectionModalOpen}
                onClose={() => setIsSelectionModalOpen(false)}
                size="lg"
                header={
                    <span className="font-semibold text-slate-800 text-sm">
                        {t(`select_${itemType}_columns`, `Select ${itemType} columns`)}
                    </span>
                }
                footer={
                    <div className="flex justify-end">
                        <Button
                            color="black"
                            onClick={() => setIsSelectionModalOpen(false)}
                        >
                            {t('apply')}
                        </Button>
                    </div>
                }
            >
                <div className="flex flex-col gap-3">
                    <TextInput
                        placeholder={t('search') + '...'}
                        leftSection={<IconSearch size={16} className="text-slate-400" />}
                        value={modalSearch}
                        onChange={(e) => setModalSearch(e.currentTarget.value)}
                        rightSection={
                            modalSearch && (
                                <button
                                    onClick={() => setModalSearch('')}
                                    className="text-slate-400 hover:text-slate-600 focus:outline-none flex items-center justify-center"
                                >
                                    <IconX size={16} />
                                </button>
                            )
                        }
                    />

                    <div className="flex justify-between items-center gap-2">
                        <span className="text-xs text-slate-500 font-medium">
                            {selectedItemIds.length} {t('selected', 'selected')}
                        </span>
                        <div className="flex gap-2">
                            <Button
                                size="xs"
                                variant="light"
                                color="black"
                                onClick={handleSelectAll}
                            >
                                {t('select_all', 'Select All')}
                            </Button>
                            <Button
                                size="xs"
                                variant="light"
                                color="red"
                                onClick={handleClearSelection}
                            >
                                {t('clear', 'Clear')}
                            </Button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto border border-slate-100 rounded-lg p-1 divide-y divide-slate-100 max-h-[350px]">
                        {filteredModalOptions.map((opt) => {
                            const isSelected = selectedItemIds.includes(opt.value);
                            return (
                                <div
                                    key={opt.value}
                                    onClick={() => toggleItem(opt.value)}
                                    className={`flex items-center gap-3 p-2.5 rounded-md cursor-pointer transition-colors duration-150 ${isSelected ? 'bg-slate-50/80 hover:bg-slate-100/80' : 'hover:bg-slate-50/50'
                                        }`}
                                >
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={() => { }} // handled by click on container
                                        color="black"
                                        tabIndex={-1}
                                        styles={{ input: { cursor: 'pointer' } }}
                                    />
                                    <div className="flex-1 min-w-0">
                                        {renderSelectOption({ option: opt })}
                                    </div>
                                </div>
                            );
                        })}
                        {filteredModalOptions.length === 0 && (
                            <div className="p-8 text-center text-xs text-slate-400 font-medium">
                                {t('no_options_found', 'No options found')}
                            </div>
                        )}
                    </div>

                </div>
            </CardModal>

        </>
    );
}
